import { createContext, use, useState, type ReactNode } from 'react'

type CartState = { editionIds: readonly string[]; offerIds: readonly string[] }
export type CartEdition = { id: string; title: string; priceCents: number }
type CartMeta = { editions: Readonly<Record<string, CartEdition>> }
type CartActions = {
  add: (edition: CartEdition) => void
  remove: (editionId: string) => void
  clear: () => void
}
type CartContextValue = { state: CartState; actions: CartActions; meta: CartMeta }

const CartContext = createContext<CartContextValue | null>(null)
const storageKey = 'momentos-cart:v2'

function loadInitialCart(): CartState {
  try {
    const value = JSON.parse(localStorage.getItem(storageKey) ?? 'null') as {
      version?: unknown
      editionIds?: unknown
    } | null
    if (value?.version !== 2 || !Array.isArray(value.editionIds)) {
      return { editionIds: [], offerIds: [] }
    }
    const editionIds = [
      ...new Set(value.editionIds.filter((id): id is string => typeof id === 'string')),
    ]
    return { editionIds, offerIds: editionIds }
  } catch {
    return { editionIds: [], offerIds: [] }
  }
}

function persist(state: CartState): void {
  try {
    localStorage.setItem(storageKey, JSON.stringify({ version: 2, editionIds: state.editionIds }))
  } catch {
    /* permanece em memória */
  }
}

export function CartProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<CartState>(loadInitialCart)
  const [editions, setEditions] = useState<Record<string, CartEdition>>({})

  function update(updater: (current: CartState) => CartState) {
    setState((current) => {
      const next = updater(current)
      persist(next)
      return next
    })
  }

  const actions: CartActions = {
    add: (edition) => {
      setEditions((current) => ({ ...current, [edition.id]: edition }))
      update((current) =>
        current.editionIds.includes(edition.id)
          ? current
          : {
              editionIds: [...current.editionIds, edition.id],
              offerIds: [...current.editionIds, edition.id],
            },
      )
    },
    remove: (editionId) => {
      setEditions((current) => {
        const next = { ...current }
        delete next[editionId]
        return next
      })
      update((current) => {
        const editionIds = current.editionIds.filter((id) => id !== editionId)
        return { editionIds, offerIds: editionIds }
      })
    },
    clear: () => {
      setEditions({})
      update(() => ({ editionIds: [], offerIds: [] }))
    },
  }

  return <CartContext value={{ state, actions, meta: { editions } }}>{children}</CartContext>
}

export function useCart(): CartContextValue {
  const value = use(CartContext)
  if (!value) throw new Error('useCart precisa estar dentro de CartProvider')
  return value
}
